import { FC } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { useFieldUpdate } from '@/hooks/useFieldUpdate';

interface DebouncedTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'onBlur' | 'value'> {
  value: string;
  onUpdate: (value: string) => void;
  updateMode?: 'debounce' | 'blur';
  debounceDelay?: number;
}

/**
 * A TextField component that handles store updates intelligently
 * to prevent performance issues from keystroke-by-keystroke updates
 *
 * - 'debounce': Update after 500ms delay OR on blur, whichever comes first (default, best for long text)
 * - 'blur': Update only on blur, no timer (good for short fields where user always tabs out)
 */
export const DebouncedTextField: FC<DebouncedTextFieldProps> = ({
  value,
  onUpdate,
  updateMode = 'debounce',
  debounceDelay = 500,
  ...textFieldProps
}) => {
  const fieldUpdate = useFieldUpdate({
    initialValue: value,
    updateStore: onUpdate,
    mode: updateMode,
    delay: debounceDelay,
  });

  return (
    <TextField
      {...textFieldProps}
      value={fieldUpdate.value}
      onChange={(e) => fieldUpdate.onChange(e.target.value)}
      onBlur={fieldUpdate.onBlur}
    />
  );
};
